import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';
import { Box, Typography, IconButton, Menu, MenuItem, CircularProgress, Tooltip, Alert } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import { EChartsOption, SeriesOption } from 'echarts';
import { exportChartToPng } from '../../utils/chartUtils';
import { exportChartToPdf, exportToCsv } from '../../utils/exportUtils';
import debounce from 'lodash/debounce';
import throttle from 'lodash/throttle';

// Type definitions
export interface BarChartDataItem {
  name: string;
  [key: string]: string | number;
}

export interface BarSeries {
  dataKey: string;
  name?: string;
  color?: string;
  stack?: string;
  isTarget?: boolean;
}

// Add support for chart.js-like data format
export interface ChartJsBarData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor: string;
  }[];
}

export interface EBarChartProps {
  /** Chart data - can be either BarChartDataItem[] or ChartJsBarData */
  data: BarChartDataItem[] | ChartJsBarData;
  /** Data series to display */
  series?: BarSeries[];
  /** Chart title */
  title?: string;
  /** Chart height */
  height?: number;
  /** X-axis label */
  xAxisLabel?: string;
  /** Y-axis label */
  yAxisLabel?: string;
  /** Show chart grid */
  grid?: boolean;
  /** Stack bars instead of placing side by side */
  stacked?: boolean;
  /** Format tooltips */
  tooltipFormatter?: (value: number | string) => string;
  /** Reference line value (e.g., for thresholds) */
  referenceLine?: { y?: number; x?: string | number; label?: string; color?: string };
  /** Data is refreshing status */
  isRefreshing?: boolean;
  /** Last updated timestamp */
  lastUpdated?: Date;
  /** Function to refresh data */
  onRefresh?: () => void;
  /** Error message to display */
  error?: string;
  /** Show fallback UI when data is empty */
  emptyStateMessage?: string;
}

const EBarChart: React.FC<EBarChartProps> = ({
  data: inputData,
  series: inputSeries,
  title,
  height = 400,
  xAxisLabel,
  yAxisLabel,
  grid = true,
  stacked = false,
  tooltipFormatter,
  referenceLine,
  isRefreshing = false,
  onRefresh,
  error,
  emptyStateMessage = 'No data available',
}) => {
  const chartRef = useRef<ReactECharts>(null);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const exportMenuOpen = Boolean(exportMenuAnchor);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(new Date());
  const [chartError, setChartError] = useState<string | null>(null);
  
  // Process data for ECharts format
  const { xAxisData, seriesData } = useMemo(() => {
    // Handle Chart.js-like data format
    if (inputData && !Array.isArray(inputData) && 'labels' in inputData && 'datasets' in inputData) {
      const xData = inputData.labels;
      const series = inputData.datasets.map(dataset => ({
        name: dataset.label,
        data: dataset.data,
        type: 'bar' as const,
        stack: stacked ? 'stack' : undefined,
        itemStyle: {
          color: dataset.backgroundColor
        }
      }));
      return { xAxisData: xData, seriesData: series };
    }
    
    // Handle standard data format
    const data = inputData as BarChartDataItem[];
    const xData = data.map(item => item.name);
    
    // Generate series if not provided
    let finalSeries = inputSeries || [];
    if (!finalSeries || finalSeries.length === 0) {
      if (data.length > 0) {
        const firstItem = data[0];
        finalSeries = Object.keys(firstItem)
          .filter(key => key !== 'name')
          .map(key => ({
            dataKey: key,
            name: key,
            color: undefined,
            stack: undefined,
            isTarget: false
          }));
      }
    }
    
    // Convert to ECharts series format
    const series = finalSeries
      .filter(s => !s.isTarget)
      .map(serie => ({
        name: serie.name || serie.dataKey,
        data: data.map(item => item[serie.dataKey]),
        type: 'bar' as const,
        stack: stacked ? 'stack' : serie.stack,
        itemStyle: {
          color: serie.color
        }
      }));

    return { xAxisData: xData, seriesData: series };
  }, [inputData, inputSeries, stacked]);
  
  // Create ECharts options with useMemo for performance  // Create ECharts options with useMemo for performance
  const options: EChartsOption = useMemo(() => ({
    title: title ? {
      text: title,
      left: 'center'
    } : undefined,
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      },
      formatter: tooltipFormatter 
        ? (params: any) => {
            const p = Array.isArray(params) ? params[0] : params;
            return tooltipFormatter(p.value as number | string);
          }
        : undefined
    },
    legend: {
      data: seriesData.map(s => s.name),
      bottom: 0
    },
    grid: {
      left: yAxisLabel ? '10%' : '3%',
      right: '4%',
      bottom: '15%',
      top: title ? '15%' : '10%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: xAxisData,
      name: xAxisLabel,
      nameLocation: 'middle',
      nameGap: 30,
      axisLabel: {
        rotate: xAxisData.length > 10 ? 45 : 0
      }
    },
    yAxis: {
      type: 'value',
      name: yAxisLabel,
      nameLocation: 'middle',
      nameGap: 50
    },
    series: seriesData,
    markLine: referenceLine?.y ? {
      data: [
        {
          name: referenceLine.label || 'Reference',
          yAxis: referenceLine.y,
          lineStyle: {
            color: referenceLine.color || 'red',
            type: 'dashed'
          },
          label: {
            show: true,
            formatter: referenceLine.label || '{c}'
          }
        }
      ]
    } : undefined
  }), [title, tooltipFormatter, seriesData, yAxisLabel, xAxisLabel, xAxisData, referenceLine, grid]);
    // Debounce the refresh handler to prevent multiple rapid calls
  const handleRefresh = useCallback(
    debounce(() => {
      if (onRefresh) {
        onRefresh();
        setLastUpdated(new Date());
      }
    }, 300), 
    [onRefresh, setLastUpdated]
  );

  const handleExportMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(event.currentTarget);
  };
  
  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };
  
  const handleExportPng = () => {
    handleExportMenuClose();
    if (chartRef.current) {
      const echartInstance = chartRef.current.getEchartsInstance();
      const dataURL = echartInstance.getDataURL();
      const link = document.createElement('a');
      link.download = `${title || 'chart'}.png`;
      link.href = dataURL;
      link.click();
    }
  };
  const handleExportPdf = () => {
    handleExportMenuClose();
    if (chartRef.current && chartRef.current.ele) {
      // Get the DOM element of the chart for PDF export
      const chartElement = chartRef.current.ele as HTMLDivElement;
      
      // Prepare additional data for the PDF
      let additionalData;
      
      if (Array.isArray(inputData)) {
        // For standard data format
        additionalData = {
          headers: ['Name', ...seriesData.map(s => s.name || '')],
          data: (inputData as BarChartDataItem[]).map(item => [
            item.name,
            ...seriesData.map(s => {
              const seriesName = s.name || '';
              return item[seriesName] || '';
            })
          ])
        };
      } else if (inputData && 'labels' in inputData && 'datasets' in inputData) {
        // For ChartJS format
        const chartJsData = inputData as ChartJsBarData;
        additionalData = {
          headers: ['Label', ...chartJsData.labels],
          data: chartJsData.datasets.map(dataset => [
            dataset.label,
            ...dataset.data
          ])
        };
      }
      
      exportChartToPdf(
        chartElement,
        title || 'chart',
        title,
        additionalData
      );
    }
  };

  const handleExportCsv = () => {
    handleExportMenuClose();
    
    // Prepare headers and data for CSV export
    let headers: string[] = [];
    let csvData: (string | number)[][] = [];
    
    if (Array.isArray(inputData)) {
      // For standard data format
      const data = inputData as BarChartDataItem[];
      headers = ['Name', ...seriesData.map(s => s.name || '')];
      csvData = data.map(item => [
        item.name,
        ...seriesData.map(s => {
          const seriesName = s.name || '';
          return item[seriesName] || '';
        })
      ]);
    } else if (inputData && 'labels' in inputData && 'datasets' in inputData) {
      // For ChartJS format
      const chartJsData = inputData as ChartJsBarData;
      headers = ['Dataset', ...chartJsData.labels];
      csvData = chartJsData.datasets.map(dataset => [
        dataset.label,
        ...dataset.data
      ]);
    }
    
    if (headers.length > 0 && csvData.length > 0) {
      exportToCsv(headers, csvData, title || 'chart_data');
    }
  };
  // Format the last updated time or show relative time (e.g., "2 minutes ago")
  const formattedLastUpdated = useMemo(() => {
    if (!lastUpdated) return '';
    
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - lastUpdated.getTime()) / 1000);
    
    if (diffInSeconds < 60) {
      return `Updated: ${diffInSeconds} seconds ago`;
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `Updated: ${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `Updated: ${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else {
      return `Updated: ${lastUpdated.toLocaleString()}`;
    }
  }, [lastUpdated]);
  
  // Reset error when data changes
  useEffect(() => {
    setChartError(null);
  }, [inputData]);
  
  // Check if data is empty
  const isEmpty = useMemo(() => {
    if (!inputData) return true;
    
    if (!Array.isArray(inputData) && 'labels' in inputData) {
      return inputData.labels.length === 0 || inputData.datasets.length === 0;
    }
    
    return Array.isArray(inputData) && inputData.length === 0;
  }, [inputData]);
  
  // Show error or empty state message if needed
  if (error || chartError) {
    return (
      <Box sx={{ width: '100%', mb: 3, position: 'relative' }}>
        {title && <Typography variant="h6" sx={{ fontWeight: 'medium', mb: 1 }}>{title}</Typography>}
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || chartError}
        </Alert>
      </Box>
    );
  }
  
  if (isEmpty) {
    return (
      <Box sx={{ width: '100%', mb: 3, position: 'relative' }}>
        {title && <Typography variant="h6" sx={{ fontWeight: 'medium', mb: 1 }}>{title}</Typography>}
        <Box 
          sx={{ 
            border: '1px solid', 
            borderColor: 'divider',
            borderRadius: 1, 
            p: 3, 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: height,
            backgroundColor: 'background.paper',
            color: 'text.secondary'
          }}
        >
          {emptyStateMessage}
        </Box>
      </Box>
    );
  }
  
  // Handle chart initialization errors
  const handleChartError = () => {
    setChartError('Failed to render chart. Please try again later.');
  };

  // Handle chart resizing to ensure responsive behavior
  useEffect(() => {
    const handleResize = throttle(() => {
      if (chartRef.current) {
        const instance = chartRef.current.getEchartsInstance();
        instance.resize();
      }
    }, 100);
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return (
    <Box sx={{ width: '100%', mb: 3, position: 'relative' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        {title && <Typography variant="h6" sx={{ fontWeight: 'medium' }}>{title}</Typography>}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {lastUpdated && (
            <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', mr: 2 }}>
              {formattedLastUpdated}
            </Box>
          )}
          
          {onRefresh && (
            <Tooltip title="Refresh data">
              <IconButton size="small" onClick={handleRefresh} disabled={isRefreshing}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
            <Tooltip title="Export options">
            <IconButton size="small" onClick={handleExportMenuOpen} aria-label="Export options">
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={exportMenuAnchor}
            open={exportMenuOpen}
            onClose={handleExportMenuClose}
            PaperProps={{
              sx: { minWidth: '200px' }
            }}
          >
            <Typography variant="subtitle2" sx={{ px: 2, py: 1, fontWeight: 'bold', color: 'text.secondary' }}>
              Export Options
            </Typography>
            
            <MenuItem onClick={handleExportPng} dense>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Box component="span" sx={{ flexGrow: 1 }}>Export as PNG</Box>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Image</Typography>
              </Box>
            </MenuItem>
            
            <MenuItem onClick={handleExportPdf} dense>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Box component="span" sx={{ flexGrow: 1 }}>Export as PDF</Box>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Document</Typography>
              </Box>
            </MenuItem>
            
            <MenuItem onClick={handleExportCsv} dense>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Box component="span" sx={{ flexGrow: 1 }}>Export as CSV</Box>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Data</Typography>
              </Box>
            </MenuItem>
          </Menu>
        </Box>
      </Box>
      <Box
        sx={{
          width: '100%',
          borderRadius: 1,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          position: 'relative'
        }}
      >
        {isRefreshing && (
          <Box 
            sx={{ 
              position: 'absolute', 
              top: 0, 
              left: 0, 
              right: 0, 
              bottom: 0, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              background: 'rgba(255, 255, 255, 0.7)', 
              borderRadius: 1, 
              zIndex: 5 
            }}
          >
            <CircularProgress size={30} />
          </Box>
        )}
          <ReactECharts
          ref={chartRef}
          option={options}
          style={{ height: height }}
          opts={{ renderer: 'canvas' }}
          notMerge={true}
          onEvents={{
            error: handleChartError
          }}
          lazyUpdate={true}
          loadingOption={{
            text: 'Loading chart...',
            textColor: '#888',
            maskColor: 'rgba(255, 255, 255, 0.8)',
            fontSize: 14
          }}
          showLoading={isRefreshing}
        />
      </Box>
    </Box>
  );
};

// Export with React.memo for performance optimization
export default React.memo(EBarChart);
